/**
 * Menu-record name uniqueness — the ONE rule, for management's client services
 * (`isNameTaken` in packages/client `items` / `menuGroups` / `modifierGroups` /
 * `modifiers`) and the server's `MenuEditService` (POS menu editor).
 *
 * The rule, unchanged from the four client copies it replaced: names compare
 * case-insensitively, the CANDIDATE is trimmed and the stored name is not, and
 * a record that is switched off still holds its name (management words that
 * conflict as "already exists but is archived" — `isActive === false`). A record
 * never conflicts with itself, decided by the caller's `isSelf`.
 *
 * Exported from `index.ts` AND `node.ts`: both planes must agree, or one surface
 * accepts the duplicate the other refuses.
 */

export interface MenuNameRecord {
    readonly name?: string | null;
    readonly isActive?: boolean | null;
}

export interface MenuNameConflict<T> {
    /** The record already holding the name. */
    readonly record: T;
    /** True when that record is switched off (`isActive === false`), which management calls "archived". */
    readonly isArchived: boolean;
}

export function findMenuNameConflict<T extends MenuNameRecord>(
    records: readonly T[],
    name: string,
    isSelf?: (record: T) => boolean,
): MenuNameConflict<T> | null {
    const normalized = name.trim().toLowerCase();
    const record = records.find((r) => (r.name ?? '').toLowerCase() === normalized && !(isSelf?.(r) ?? false));
    return record ? { record, isArchived: record.isActive === false } : null;
}
